import { type FC, useState } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";
import { sentryUtils } from "@/shared/utils/sentry";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import {
  SafeDepositForm,
  EMPTY_SAFE_DEPOSIT_FORM,
  validateSafeDepositFields,
  safeDepositToItemFields,
  type SafeDepositFormValues,
  type SafeDepositFieldErrors,
} from "./SafeDepositForm";

export const AddSafeDeposit: FC<FlowCustomComponentProps> = ({ goToScreen, answers }) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { addItem, isAddingItem } = useEstateItems(estateId);

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    SafeDepositFormValues,
    SafeDepositFieldErrors
  >(EMPTY_SAFE_DEPOSIT_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (isSubmitting || isAddingItem) return;

    const errors = validateSafeDepositFields(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    if (!estateId) return;

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      const fields = safeDepositToItemFields(form);

      await addItem({
        estate_id: estateId,
        ...fields,
        balance: 0,
      });

      goToScreen("safe_deposit_step", {
        ...answers,
        safe_deposit_review: "has_items",
      });
    } catch (err) {
      setError("Failed to add item. Please try again.");
      sentryUtils.captureError(err, "AddSafeDeposit submission failed", {
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-center">Add safe or deposit box</h2>
      <p className="mt-2 text-center text-secondary">
        Add what you know. We'll help you gather details later.
      </p>
      <div className="mt-6 w-full max-w-[600px]">
        <AddItemLayout
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          error={error}
          disabled={isAddingItem}
        >
          <SafeDepositForm form={form} onChange={updateForm} fieldErrors={fieldErrors} />
        </AddItemLayout>
      </div>
    </div>
  );
};
